import type { UIMessage } from 'ai';
import { getRecoveredPackageErrorToolCallIds } from './recovered-package-errors';
import {
  type ToolPartData,
  asToolPart,
  getToolName,
  hasToolResultFailure,
} from './tool-parts';

const record = (value: unknown): Record<string, unknown> | undefined =>
  value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;
const nonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0;

function sameValue(left: unknown, right: unknown): boolean {
  if (left === right) return true;
  if (Array.isArray(left) && Array.isArray(right)) {
    return (
      left.length === right.length &&
      left.every((value, index) => sameValue(value, right[index]))
    );
  }
  const a = record(left);
  const b = record(right);
  if (!a || !b) return false;
  const keys = Object.keys(a);
  return (
    keys.length === Object.keys(b).length &&
    keys.every((key) => Object.hasOwn(b, key) && sameValue(a[key], b[key]))
  );
}

function campaignIntent(input: Record<string, unknown>) {
  // Adding the missing form repairs this preflight; suppressCard is display
  // only. Every other supplied field must describe the same request.
  const { leadFormId: _form, suppressCard: _display, ...intent } = input;
  return intent;
}

/**
 * Hide only the prominent error for a recovered, side-effect-free preflight.
 * Callers retain the original parts in ToolTrace. Scope to this assistant
 * message: another user request or a partial creation is not retry evidence.
 */
export function getRecoveredCampaignErrorToolCallIds(
  parts: UIMessage['parts']
): Set<string> {
  const recovered = getRecoveredPackageErrorToolCallIds(parts);
  const failures: ToolPartData[] = [];
  for (const part of parts) {
    const tool = asToolPart(part);
    if (
      !tool ||
      getToolName(tool) !== 'meta_ads_createCampaign' ||
      tool.state !== 'output-available'
    )
      continue;
    const input = record(tool.input);
    const output = record(tool.output);
    if (
      !input ||
      !output ||
      input.followUpType !== 'lead_form' ||
      !nonEmptyString(input.name)
    )
      continue;
    if (
      output.uiState === 'error' &&
      output.errorCode === 'lead_form_required' &&
      hasToolResultFailure(tool) &&
      input.leadFormId === undefined &&
      output.campaignId == null
    ) {
      failures.push(tool);
      continue;
    }
    if (
      hasToolResultFailure(tool) ||
      tool.errorText ||
      output.uiState !== 'created' ||
      output.status !== 'paused' ||
      !nonEmptyString(output.campaignId) ||
      output.name !== input.name ||
      !nonEmptyString(input.leadFormId)
    )
      continue;
    if (
      typeof input.dailyBudgetAmount === 'number' &&
      output.dailyBudget !== Math.round(input.dailyBudgetAmount * 100)
    )
      continue;
    for (const failed of failures) {
      if (sameValue(campaignIntent(failed.input), campaignIntent(input)))
        recovered.add(failed.toolCallId);
    }
  }
  return recovered;
}

function withoutCreateChoice(input: Record<string, unknown>) {
  const { createAnyway: _choice, suppressCard: _display, ...request } = input;
  return request;
}

/**
 * The duplicate chooser is a continuation, not a new campaign brief. Bridge
 * only its exact reply and immediately following result; never search across
 * unrelated user turns. An existing-candidates result alone proves no create.
 */
export function getContinuedCampaignErrorToolCallIds(
  messages: UIMessage[]
): Map<string, ReadonlySet<string>> {
  const recovered = new Map<string, ReadonlySet<string>>();
  for (let index = 0; index + 2 < messages.length; index++) {
    const [previous, choice, continuation] = messages.slice(index, index + 3);
    if (
      previous.role !== 'assistant' ||
      choice.role !== 'user' ||
      continuation.role !== 'assistant'
    )
      continue;
    const choosers = previous.parts.flatMap((part, partIndex) => {
      const tool = asToolPart(part);
      return tool &&
        getToolName(tool) === 'meta_ads_createCampaign' &&
        tool.output?.uiState === 'existing_candidates'
        ? [{ tool, partIndex }]
        : [];
    });
    // The text reply carries a proposed name, not the card's unique ID.
    if (choosers.length !== 1) continue;
    const { tool: chooser, partIndex } = choosers[0];
    const input = record(chooser.input);
    const output = record(chooser.output);
    const card = record(output?.presentation);
    if (
      chooser.state !== 'output-available' ||
      hasToolResultFailure(chooser) ||
      chooser.errorText ||
      !input ||
      input.followUpType !== 'lead_form' ||
      !nonEmptyString(input.name) ||
      !nonEmptyString(input.leadFormId) ||
      output?.campaignId != null ||
      output?.proposedName !== input.name ||
      card?.type !== 'existing_candidates' ||
      card.kind !== 'campaign' ||
      card.proposedName !== input.name ||
      !Array.isArray(card.candidates) ||
      card.candidates.length === 0 ||
      !card.candidates.every(
        (candidate) =>
          nonEmptyString(record(candidate)?.id) &&
          nonEmptyString(record(candidate)?.name)
      )
    )
      continue;
    // Exact standard ExistingCandidatesCard response: extra instructions or
    // attachments make this a new request, not evidence for this bridge.
    if (
      choice.parts.length !== 1 ||
      choice.parts[0].type !== 'text' ||
      choice.parts[0].text !==
        `Create a new campaign anyway called "${input.name}".`
    )
      continue;
    const failures = previous.parts.slice(0, partIndex).filter((part) => {
      const tool = asToolPart(part);
      return tool && hasToolResultFailure(tool);
    });
    const matches = continuation.parts.flatMap((part) => {
      const tool = asToolPart(part);
      const continuedInput = record(tool?.input);
      if (
        !tool ||
        getToolName(tool) !== 'meta_ads_createCampaign' ||
        !continuedInput ||
        continuedInput.createAnyway !== true ||
        !sameValue(
          withoutCreateChoice(input),
          withoutCreateChoice(continuedInput)
        )
      )
        return [];
      // Reuse the same strict created/paused, budget and failure predicates.
      // Only the explicit chooser permits the new createAnyway flag.
      const { createAnyway: _choice, ...retryInput } = continuedInput;
      const matchingFailures = getRecoveredCampaignErrorToolCallIds([
        ...failures,
        { ...part, input: retryInput } as UIMessage['parts'][number],
      ]);
      return matchingFailures.size ? [matchingFailures] : [];
    });
    if (matches.length === 1) recovered.set(previous.id, matches[0]);
  }
  return recovered;
}
